import { useState } from "react";
import { FieldErrors, UseFormRegister } from "react-hook-form";

import { Button, Input } from "components/common/basic";
import { AddressFragment, FreelanceProfileFragment } from "graphql/fragments";
import { Trans, useTranslation } from "translations";
import { tw } from "utils/tw";

export interface Props {
  register: UseFormRegister<FreelanceProfileFragment>;
  address: AddressFragment;
  addressErrors?: FieldErrors<AddressFragment>;
}

export default ({
  register,
  address,
  addressErrors,
}: Props): React.JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation("common");

  return (
    <div className={tw("w-full", "space-y-4")}>
      <div className={tw("flex", "justify-between", "items-center")}>
        <h2 className={tw("text-lg", "font-bold")}>
          <Trans
            ns="common"
            i18nKey="popup.freelanceProfile_organization.addressSection.title"
          >
            Organisation address
          </Trans>
        </h2>

        {!isEditing && (
          <Button
            id="address_section-edit_address"
            variant="tertiary"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <Trans
              ns="common"
              i18nKey="popup.freelanceProfile_organization.addressSection.button.edit"
            >
              Edit address
            </Trans>
          </Button>
        )}
      </div>

      {!isEditing ? (
        <div>
          <p>{address.line1}</p>
          <p>{address.line2}</p>
          <p>{address.postalCode}</p>
          <p>{address.city}</p>
          <p>{address.county}</p>
          <p>{address.country}</p>
        </div>
      ) : (
        <div className={tw("space-y-4")}>
          <Input
            id="address.line1"
            {...register("address.line1")}
            label={t(
              "popup.freelanceProfile_organization.addressSection.line1.label",
              "Address"
            )}
            placeholder={t(
              "popup.freelanceProfile_organization.addressSection.line1.placeholder",
              "Address"
            )}
            errorMessage={addressErrors?.line1?.message}
          />

          <Input
            id="address.line2"
            {...register("address.line2")}
            label={t(
              "popup.freelanceProfile_organization.addressSection.line2.label",
              "Address line 2"
            )}
            placeholder={t(
              "popup.freelanceProfile_organization.addressSection.line2.placeholder",
              "Address line 2"
            )}
            errorMessage={addressErrors?.line2?.message}
          />

          <div className={tw("flex", "space-x-4")}>
            <div className={tw("w-1/3")}>
              <Input
                id="address.postalCode"
                {...register("address.postalCode")}
                label={t(
                  "popup.freelanceProfile_organization.addressSection.postalCode.label",
                  "Post code"
                )}
                placeholder={t(
                  "popup.freelanceProfile_organization.addressSection.postalCode.placeholder",
                  "Post code"
                )}
                errorMessage={addressErrors?.postalCode?.message}
              />
            </div>

            <div className={tw("w-2/3")}>
              <Input
                id="address.city"
                {...register("address.city")}
                label={t(
                  "popup.freelanceProfile_organization.addressSection.city.label",
                  "City"
                )}
                placeholder={t(
                  "popup.freelanceProfile_organization.addressSection.city.placeholder",
                  "City"
                )}
                errorMessage={addressErrors?.city?.message}
              />
            </div>
          </div>

          <Input
            id="address.county"
            {...register("address.county")}
            label={t(
              "popup.freelanceProfile_organization.addressSection.county.label",
              "County"
            )}
            placeholder={t(
              "popup.freelanceProfile_organization.addressSection.county.placeholder",
              "County"
            )}
            errorMessage={addressErrors?.county?.message}
          />

          <Input
            id="address.country"
            {...register("address.country")}
            label={t(
              "popup.freelanceProfile_organization.addressSection.country.label",
              "Country"
            )}
            readOnly
          />
        </div>
      )}
    </div>
  );
};
